/** Email campaigns: list, editor (blocks + live preview), test, schedule / send now, report. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Button, Col, DatePicker, Drawer, Dropdown, Empty, Input, Modal, Popconfirm, Progress, Row, Select, Space, Spin, Statistic, Table, Tag, Typography, message,
} from 'antd';
import { CopyOutlined, DeleteOutlined, EyeOutlined, MoreOutlined, PlusOutlined, SendOutlined, BarChartOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from '../../../shared/i18n';
import { fmtDate, listOf, money, totalOf, useSite } from './common';
import { BlockEditor, EmailPreview, PreviewModal, TestModal, mktError } from './MarketingCommon';

const { Text } = Typography;
const STATUS = {
    draft: ['default', 'Draft'], scheduled: ['blue', 'Scheduled'], sending: ['processing', 'Sending'], paused: ['orange', 'Paused'],
    sent: ['green', 'Sent'], cancelled: ['default', 'Cancelled'], failed: ['red', 'Failed'],
};
export const CampaignStatus = ({ s }) => {
    const { t } = useTranslation();
    const [c, l] = STATUS[s] || ['default', s];
    return <Tag color={c}>{t(l)}</Tag>;
};

const pct = (v) => (v === null || v === undefined ? '-' : `${Math.round(Number(v) * 10) / 10}%`); // backend sends percent (0–100)

const Report = ({ open, id, onClose }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [d, setD] = useState(null);
    useEffect(() => { if (open && id) { setD(null); api.get(`/marketing/campaigns/${id}/report`).then(setD).catch((e) => { mktError(e); onClose(); }); } }, [open, id]); // eslint-disable-line react-hooks/exhaustive-deps
    const s = d?.stats || {};
    return (
        <Drawer open={open} onClose={onClose} width={Math.min(720, window.innerWidth)} title={d?.name || t('Report')}>
            {!d ? <Spin /> : (
                <>
                    <Row gutter={[16, 16]}>
                        <Col span={8}><Statistic title={t('Sent', { context: 'shop' })} value={s.sent || 0} /></Col>
                        <Col span={8}><Statistic title={t('Open rate')} value={pct(s.open_rate)} /></Col>
                        <Col span={8}><Statistic title={t('Click rate')} value={pct(s.click_rate)} /></Col>
                        <Col span={8}><Statistic title={t('Orders')} value={s.orders || 0} /></Col>
                        <Col span={8}><Statistic title={t('Revenue')} value={money(s.revenue, s.currency)} /></Col>
                        <Col span={8}><Statistic title={t('Unsubscribed')} value={s.unsubscribed || 0} /></Col>
                    </Row>
                    <Space wrap style={{ margin: '12px 0' }}>
                        <Text type="secondary">{t('Delivered')}: {s.delivered || 0}</Text>
                        <Text type="secondary">{t('Bounced')}: {s.bounced || 0}</Text>
                        <Text type="secondary">{t('Complaints')}: {s.complained || 0}</Text>
                        {s.queued > 0 && <Text type="secondary">{t('Queued')}: {s.queued}</Text>}
                    </Space>
                    {d.recipients > 0 && s.sent < d.recipients && <Progress percent={Math.round((s.sent / d.recipients) * 100)} size="small" />}
                    <Table size="small" rowKey="url" pagination={false} dataSource={d.links || []} style={{ marginTop: 12 }}
                        columns={[
                            { title: t('Link'), dataIndex: 'url', render: (v) => <Text ellipsis style={{ maxWidth: 420 }}>{v}</Text> },
                            { title: t('Clicks'), dataIndex: 'clicks', width: 80, align: 'right' },
                            { title: t('Unique'), dataIndex: 'unique_clicks', width: 80, align: 'right' },
                        ]} />
                </>
            )}
        </Drawer>
    );
};

const Editor = ({ id, onClose, onSaved, goDomain }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [c, setC] = useState(null);
    const [segments, setSegments] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [html, setHtml] = useState('');
    const [testOpen, setTestOpen] = useState(false);
    const [schedOpen, setSchedOpen] = useState(false);
    const [at, setAt] = useState(null);
    const timer = useRef(null);

    useEffect(() => {
        api.get(`/marketing/campaigns/${id}`).then(setC).catch((e) => { mktError(e); onClose(); });
        api.get('/marketing/segments').then((d) => setSegments(listOf(d))).catch(() => {});
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshPreview = useCallback((doc) => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            api.post(`/marketing/campaigns/${id}/preview`, { subject: doc.subject, preheader: doc.preheader, content: doc.content }, { skipGlobalErrorToast: true })
                .then((r) => setHtml(r?.html || '')).catch(() => {});
        }, 700);
    }, [api, id]);
    useEffect(() => { if (c) refreshPreview(c); }, [c, refreshPreview]);

    const set = (patch) => { setC((x) => ({ ...x, ...patch })); setDirty(true); };
    const save = async () => {
        setSaving(true);
        try {
            const r = await api.put(`/marketing/campaigns/${id}`, { name: c.name, subject: c.subject, preheader: c.preheader, segment_id: c.segment_id || 0, content: c.content });
            setC((x) => ({ ...x, ...r }));
            setDirty(false);
            onSaved?.();
            return true;
        } catch (e) { mktError(e, { goDomain }); return false; } finally { setSaving(false); }
    };
    const schedule = async (when) => {
        if (dirty && !(await save())) return;
        try {
            await api.post(`/marketing/campaigns/${id}/schedule`, when ? { at: when } : {});
            message.success(when ? t('Scheduled') : t('Sending started'));
            setSchedOpen(false);
            onSaved?.();
            onClose();
        } catch (e) { mktError(e, { goDomain }); }
    };
    if (!c) return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;
    const editable = ['draft', 'paused', 'scheduled'].includes(c.status);
    return (
        <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <Input value={c.name} onChange={(e) => set({ name: e.target.value })} style={{ maxWidth: 320, fontWeight: 600 }} disabled={!editable} />
                <CampaignStatus s={c.status} />
                <div style={{ flex: 1 }} />
                <Button onClick={() => setTestOpen(true)}>{t('Send test')}</Button>
                <Button onClick={save} loading={saving} disabled={!dirty}>{t('Save')}</Button>
                {c.status === 'draft' && <Button type="primary" icon={<SendOutlined />} onClick={() => setSchedOpen(true)}>{t('Send')}</Button>}
            </div>
            <Row gutter={16}>
                <Col xs={24} lg={12}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Input addonBefore={t('Subject')} value={c.subject} onChange={(e) => set({ subject: e.target.value })} maxLength={255} disabled={!editable} />
                        <Input addonBefore={t('Preview text')} value={c.preheader} onChange={(e) => set({ preheader: e.target.value })} maxLength={255} disabled={!editable} />
                        <Select style={{ width: '100%' }} value={c.segment_id || 0} disabled={!editable} onChange={(segment_id) => set({ segment_id })}
                            options={[{ value: 0, label: t('All subscribers') }, ...segments.map((s) => ({ value: s.id, label: `${s.name}${s.count !== undefined ? ` · ${s.count}` : ''}` }))]} />
                        <BlockEditor value={c.content} onChange={(content) => set({ content })} types={['heading', 'text', 'image', 'button', 'products', 'discount', 'divider', 'spacer', 'social']} />
                    </Space>
                </Col>
                <Col xs={24} lg={12}>
                    <div style={{ position: 'sticky', top: 0 }}><EmailPreview html={html} height={720} /></div>
                </Col>
            </Row>
            <TestModal open={testOpen} onClose={() => setTestOpen(false)} onSend={async (to) => {
                if (dirty && !(await save())) throw new Error('save');
                try { await api.post(`/marketing/campaigns/${id}/test`, { to }); } catch (e) { mktError(e, { goDomain }); throw e; }
            }} />
            <Modal open={schedOpen} onCancel={() => setSchedOpen(false)} title={t('Send')} footer={null} destroyOnHidden>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Popconfirm title={t('Send to all recipients now?')} onConfirm={() => schedule(null)}>
                        <Button type="primary" block icon={<SendOutlined />}>{t('Send now')}</Button>
                    </Popconfirm>
                    <Space.Compact style={{ width: '100%' }}>
                        <DatePicker showTime value={at} onChange={setAt} style={{ flex: 1 }} disabledDate={(d) => d && d < dayjs().startOf('day')} />
                        <Button disabled={!at} onClick={() => schedule(at.format('YYYY-MM-DD HH:mm:ss'))}>{t('Schedule', { context: 'shop' })}</Button>
                    </Space.Compact>
                </Space>
            </Modal>
        </>
    );
};

const MarketingCampaigns = ({ goDomain }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [editing, setEditing] = useState(null);
    const [report, setReport] = useState(null);
    const [preview, setPreview] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try { const d = await api.get('/marketing/campaigns', { page, per_page: 20 }); setData({ items: listOf(d), total: totalOf(d) }); } catch (e) { mktError(e); } finally { setLoading(false); }
    }, [api, page]);
    useEffect(() => { load(); }, [load]);

    const create = async () => {
        try { const c = await api.post('/marketing/campaigns', { name: t('New campaign') }); setEditing(c.id); load(); } catch (e) { mktError(e, { goDomain }); }
    };
    const act = async (c, action) => {
        try {
            if (action === 'delete') await api.del(`/marketing/campaigns/${c.id}`);
            else if (action === 'duplicate') { const n = await api.post(`/marketing/campaigns/${c.id}/duplicate`); setEditing(n.id); } else await api.post(`/marketing/campaigns/${c.id}/${action}`);
            load();
        } catch (e) { mktError(e, { goDomain }); }
    };

    return (
        <>
            <div style={{ textAlign: 'right', marginBottom: 12 }}><Button type="primary" icon={<PlusOutlined />} onClick={create}>{t('New campaign')}</Button></div>
            <Table rowKey="id" size="small" loading={loading} dataSource={data.items} scroll={{ x: 760 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No campaigns yet')} /> }}
                pagination={{ current: page, pageSize: 20, total: data.total, onChange: setPage, hideOnSinglePage: true }}
                onRow={(c) => ({ onClick: () => (['draft', 'paused', 'scheduled'].includes(c.status) ? setEditing(c.id) : setReport(c.id)), style: { cursor: 'pointer' } })}
                columns={[
                    { title: t('Campaign'), key: 'n', render: (_, c) => <Space direction="vertical" size={0}><Text strong>{c.name}</Text><Text type="secondary" style={{ fontSize: 12 }}>{c.subject || '-'}</Text></Space> },
                    { title: t('Status'), dataIndex: 'status', width: 110, render: (s, c) => <Space direction="vertical" size={0}><CampaignStatus s={s} />{c.status_reason && <Text type="secondary" style={{ fontSize: 11 }}>{c.status_reason}</Text>}</Space> },
                    { title: t('Sent', { context: 'shop' }), key: 'sent', width: 90, align: 'right', render: (_, c) => c.stats?.sent || 0 },
                    { title: t('Opens'), key: 'o', width: 80, align: 'right', render: (_, c) => pct(c.stats?.open_rate) },
                    { title: t('Clicks'), key: 'c', width: 80, align: 'right', render: (_, c) => pct(c.stats?.click_rate) },
                    { title: t('Revenue'), key: 'r', width: 110, align: 'right', render: (_, c) => money(c.stats?.revenue || 0, c.stats?.currency) },
                    { title: t('Date'), key: 'd', width: 160, render: (_, c) => fmtDate(c.scheduled_at || c.started_at || c.created_at) },
                    {
                        title: '', key: 'a', width: 50, render: (_, c) => (
                            <span onClick={(e) => e.stopPropagation()}>
                                <Dropdown trigger={['click']} menu={{
                                    items: [
                                        { key: 'preview', icon: <EyeOutlined />, label: t('Preview') },
                                        { key: 'report', icon: <BarChartOutlined />, label: t('Report') },
                                        { key: 'duplicate', icon: <CopyOutlined />, label: t('Duplicate') },
                                        c.status === 'scheduled' && { key: 'unschedule', label: t('Unschedule') },
                                        c.status === 'sending' && { key: 'pause', label: t('Pause') },
                                        c.status === 'paused' && { key: 'resume', label: t('Resume') },
                                        ['scheduled', 'sending', 'paused'].includes(c.status) && { key: 'cancel', danger: true, label: t('Cancel sending') },
                                        ['draft', 'cancelled', 'sent'].includes(c.status) && { key: 'delete', danger: true, icon: <DeleteOutlined />, label: t('Delete') },
                                    ].filter(Boolean),
                                    onClick: ({ key }) => {
                                        if (key === 'preview') setPreview(c.id);
                                        else if (key === 'report') setReport(c.id);
                                        else if (key === 'delete' || key === 'cancel') Modal.confirm({ title: key === 'delete' ? t('Delete?') : t('Stop sending?'), onOk: () => act(c, key) });
                                        else act(c, key);
                                    },
                                }}><Button size="small" type="text" icon={<MoreOutlined />} /></Dropdown>
                            </span>
                        ),
                    },
                ]} />
            <Drawer open={!!editing} onClose={() => { setEditing(null); load(); }} width="min(1200px, 100vw)" title={t('Campaign')} destroyOnHidden>
                {editing && <Editor id={editing} onClose={() => { setEditing(null); load(); }} onSaved={load} goDomain={() => { setEditing(null); goDomain?.(); }} />}
            </Drawer>
            <Report open={!!report} id={report} onClose={() => setReport(null)} />
            <PreviewModal open={!!preview} onClose={() => setPreview(null)} loader={() => api.post(`/marketing/campaigns/${preview}/preview`, {})} />
        </>
    );
};

export default MarketingCampaigns;
